import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  AiStatus,
  AiSuggestion,
  AiSuggestionList,
  type CreateAiSuggestionBody,
} from '@klunkers/shared'
import { api } from '@/api/http'

const POLL_MS = 2500
const GIVE_UP_MS = 3 * 60_000

/**
 * AI suggestions (split a note, identify a part, diagnose). Results arrive
 * over socket.io (see App.vue), with polling as a fallback. Everything here
 * is a suggestion: components show it, the rider acts on it.
 */
export const useAiStore = defineStore('ai', () => {
  const status = ref<AiStatus | null>(null)
  const byId = ref<Record<string, AiSuggestion>>({})
  const enabled = computed(() => status.value?.enabled ?? false)

  async function loadStatus() {
    status.value = await api('/ai/status', { schema: AiStatus }).catch(() => null)
  }

  async function refresh(id: string) {
    const s = await api(`/ai/suggestions/${id}`, { schema: AiSuggestion })
    byId.value = { ...byId.value, [id]: s }
    return s
  }

  function follow(id: string) {
    const started = Date.now()
    const tick = async () => {
      const s = byId.value[id]
      if (!s || s.state !== 'pending' || Date.now() - started > GIVE_UP_MS) return
      await refresh(id).catch(() => {})
      setTimeout(() => void tick(), POLL_MS)
    }
    setTimeout(() => void tick(), POLL_MS)
  }

  async function request(body: CreateAiSuggestionBody): Promise<AiSuggestion> {
    const s = await api('/ai/suggestions', { method: 'POST', body, schema: AiSuggestion })
    byId.value = { ...byId.value, [s.id]: s }
    if (status.value) status.value = { ...status.value, used_today: status.value.used_today + 1 }
    follow(s.id)
    return s
  }

  /** The newest suggestion about a note, photo or bike, if any. */
  async function latest(
    subjectType: 'capture' | 'photo' | 'bike',
    subjectId: string,
    kind?: AiSuggestion['kind'],
  ) {
    const { suggestions } = await api(
      `/ai/suggestions?subject_type=${subjectType}&subject_id=${subjectId}`,
      {
        schema: AiSuggestionList,
      },
    )
    const s = suggestions.find((x) => !kind || x.kind === kind)
    if (s) {
      byId.value = { ...byId.value, [s.id]: s }
      if (s.state === 'pending') follow(s.id)
    }
    return s ?? null
  }

  return { status, enabled, byId, loadStatus, refresh, request, latest }
})
